import React, { useMemo } from 'react';
import { useSelector } from "react-redux";
import { Row } from 'react-bootstrap';
import moment from 'moment';
import { Workbook } from 'react-excel-workbook';
import { DISPLAY_DATE_FORMAT2, HARVESTING_WORK_TYPE_ID, HARVEST_WORK_TYPE_IDS, WORK_TYPE_LIST } from '../../../constants';
import { isPlannedOrTodate, isToday } from '../../../helpers/common';
import { useCalculateKPI } from '../Fields/utils';
import TabToggle from './TabToggle';
import Refresh from './Refresh';
import { formatNumber, Unit } from '../../../helpers/unit';

const Header = (props) => {
    const farmName = useSelector(state => state.farm.currentFarm.farm_name);
    const farmId = useSelector(state => state.farm.currentFarm.farm_uuid);
    const searchParams = useSelector(state => state.analyze.searchParams);
    const range = useSelector(state => state.analyze.searchParams.range);
    const activeTab = useSelector(state => state.dashboard.activeTab);
    const selectedFieldIds = useSelector(state => state.dashboard.field.selectedFieldIds);
    const selectedWorkTypeIds = useSelector(state => state.dashboard.field.selectedWorkTypeIds);
    const settings = useSelector(state => state.user.settings);
    const fieldsListById = useSelector(state => state.dashboard.field.fieldListById.payload);
    const findLoads = useSelector(state => state.dashboard.field.fieldLoads.payload);
    const timezoneIdentifier = useSelector(state => state.farm.currentFarm.timezone_identifier);
    const workedAcresById = useSelector(state => state.dashboard.field.workedAcresById.payload);
    const isRequestingFieldLoads = useSelector(state => state.dashboard.field.fieldLoads.isRequesting);
    const isRequestingWorkInfo = useSelector(state => state.dashboard.field.workedAcresById.isRequesting);
    const workedAcresRequest = useSelector(state => state.dashboard.field.workedAcresById.request);
    const isEnableHarvestProjection = useSelector(state => state.farm.currentFarm.is_harvest_projection_enable);
    const harvestSummary = useSelector(state => state.dashboard.harvestSummary);
    const selectedPlanType = useSelector(state => state.plan.selectedPlanType);
    const isAllPlan = useSelector(state => state.plan.isSelectAllPlan);

    const unit = useMemo(() => Unit.getInstance(), []);

    let fieldsExport = {};
    const { getWorkedAcres, getAverageYield, isFieldCompleted } = useCalculateKPI({
        timezoneIdentifier, workedAcresById, workedAcresRequest
    });
    const getCsvRows = () => selectedWorkTypeIds.length === 1 ?
        selectedFieldIds.map((fieldId, i) => {
            if (fieldsListById[fieldId]) {
                const shapeDetails = fieldsListById[fieldId];
                const workTypeId = selectedWorkTypeIds[0];
                let workedAcres = String(getWorkedAcres(workTypeId, fieldId));
                const isFieldCompletedStatus = isFieldCompleted(workTypeId, shapeDetails);
                const loadDetail = {};
                if (workTypeId === HARVESTING_WORK_TYPE_ID) {
                    if (isEnableHarvestProjection) {
                        const geoRegionId = shapeDetails && shapeDetails.regionDetails ? shapeDetails.regionDetails.geo_region_uuid : null;
                        if (geoRegionId) {
                            const harvestSummaryList = harvestSummary.payload;
                            let harvestList = harvestSummaryList && harvestSummaryList.hasOwnProperty(geoRegionId) ? harvestSummaryList[geoRegionId] : null;
                            workedAcres = String(getSummary(harvestList, "worked"));
                            const loads = unit.toFixedOf(getSummary(harvestList, "load"));
                            loadDetail.loads = String(loads);
                            loadDetail.averageYieldAcre = String(getSummary(harvestList, "worked") === 0 ? 0 :
                                formatNumber(unit.toFixedOf(loads / getSummary(harvestList, "worked"), 0)));
                        } else {
                            workedAcres = "0";
                            loadDetail.loads = "0";
                            loadDetail.averageYieldAcre = "0";
                        }
                    }
                    else {
                        const loadDetails = findLoads && findLoads.filter(e => e.shapeRefId === fieldId);
                        const loadCapacity = loadDetails.length > 0 ? loadDetails[0].loadCapacity : 0;
                        loadDetail.loads = String(loadDetails.length > 0 ? loadDetails[0].loads : 0);
                        loadDetail.averageYieldAcre = String(getAverageYield(workTypeId, shapeDetails, loadCapacity));
                    }
                }
                return {
                    id: i + 1,
                    fieldName: fieldsListById[fieldId].geo_shape_name,
                    completedAcres: workedAcres,
                    ...loadDetail,
                    status: isFieldCompletedStatus ? "Marked Complete" : "In Progess"
                };
            }
            return null;
        }) : [];


    const getSummary = (harvestList, type) => {
        if (harvestList && harvestList.hasOwnProperty('summary')) {
            if (type === "worked") {
                let worked = 0;
                if (harvestList.summary.hasOwnProperty('harvest_acreage')) worked = getHarvestValue(harvestList, "harvest_acreage");
                if (harvestList.summary.hasOwnProperty('harvested_acreage')) worked = getHarvestValue(harvestList, "harvested_acreage");
                return worked !== 0 ? unit.toArea(worked, 0) : 0;
            }
            if (type === "percentage") {
                let worked = 0;
                if (harvestList.summary.hasOwnProperty('harvest_acreage')) worked = unit.toArea(getHarvestValue(harvestList, "harvest_acreage"), 0);
                if (harvestList.summary.hasOwnProperty('harvested_acreage')) worked = unit.toArea(getHarvestValue(harvestList, "harvested_acreage"), 0);
                return worked !== 0 ? ((worked / unit.toArea(getHarvestValue(harvestList, "total_acreage"), 0)) * 100).toFixed(0) : 0;
            }
            if (type === "load") {
                let harvesteLoads = 0;
                if (harvestList.summary.hasOwnProperty('harvest_loads')) harvesteLoads = getHarvestValue(harvestList, "harvest_loads");
                if (harvestList.summary.hasOwnProperty('harvested_loads')) harvesteLoads = getHarvestValue(harvestList, "harvested_loads");
                return harvesteLoads;
            }
        } else {
            return 0;
        }
    };

    const getHarvestValue = (harvestList, key) => {
        let harvestSummary = harvestList && harvestList.hasOwnProperty('summary') ? harvestList.summary : null;
        if (!harvestSummary) return 0;
        return harvestList.summary[key] ? harvestList.summary[key] : 0;
    };

    const getExcelFileName = () => {
        const { range, custom } = searchParams;
        const isAll = Object.keys(fieldsListById).length === selectedFieldIds.length;
        let dateRange = null;
        if (isPlannedOrTodate(range)) {
            dateRange = range;
        }
        else if (moment.utc(custom.from).format(DISPLAY_DATE_FORMAT2) === moment.utc(custom.to).format(DISPLAY_DATE_FORMAT2))
            dateRange = `${moment.utc(custom.from).format(DISPLAY_DATE_FORMAT2)}`;
        else dateRange = `${moment.utc(custom.from).format(DISPLAY_DATE_FORMAT2)} to ${moment.utc(custom.to).format(DISPLAY_DATE_FORMAT2)}`;
        return `${isAll ? "All_" : ""}Fields_${farmName.replace(/(\W+)/gi, '_')}_${dateRange}.xlsx`;
    };

    const getWorkSheets = () => {
        let selectedType = [];

        if (HARVEST_WORK_TYPE_IDS && HARVEST_WORK_TYPE_IDS.includes(selectedWorkTypeIds[0])) selectedType = WORK_TYPE_LIST[3].list.filter(e => e.work_type_id === selectedWorkTypeIds[0]);
        else selectedType = WORK_TYPE_LIST.filter(e => e.work_type_id === selectedWorkTypeIds[0]);
        const sheetTitle = selectedType && selectedType.length > 0 ? selectedType[0].work_type : '';

        if (selectedWorkTypeIds.length === 1 && selectedWorkTypeIds[0] === HARVESTING_WORK_TYPE_ID) {
            fieldsExport = {
                [sheetTitle]: {
                    title: sheetTitle,
                    header: [
                        { key: "id", label: "#" },
                        { key: "fieldName", label: "Field" },
                        { key: "completedAcres", label: `Completed (${settings.areaShortName})` },
                        { key: "loads", label: "Loads" },
                        { key: "averageYieldAcre", label: `Average Yield (${settings.weightShortName})` },
                        { key: "status", label: "Status" },
                    ]
                }
            };
        } else {
            fieldsExport = {
                [sheetTitle]: {
                    title: sheetTitle,
                    header: [
                        { key: "id", label: "#" },
                        { key: "fieldName", label: "Field" },
                        { key: "completedAcres", label: `Completed (${settings.areaShortName})` },
                        { key: "status", label: "Status" },
                    ]
                }
            };
        }

        const csvRows = getCsvRows();
        return Object.keys(fieldsExport).map(type => {
            const { title, header } = fieldsExport[type];
            return <Workbook.Sheet key={type} data={csvRows} name={title}>
                {header.map((item, i) => <Workbook.Column key={i} value={item.key} label={item.label} />)}
            </Workbook.Sheet>;
        });
    };

    const handleLoadHistory = () => {
        const newTabUrl = `/home/farm/${farmId}/loadhistory`;
        window.open(newTabUrl, "_blank");
    };

    const handleSeedHistory = () => {
        const newTabUrl = `/home/farm/${farmId}/seedhistory`;
        window.open(newTabUrl, "_blank");
    };

    const handleLogisticHistory = () => {
        const newTabUrl = `/home/farm/${farmId}/logisticshistory`;
        window.open(newTabUrl, "_blank");
    };

    const disableCondition = (selectedFieldIds && selectedFieldIds.length === 0)
        || isRequestingFieldLoads || isRequestingWorkInfo;

    return <Row className="header">
        <TabToggle {...props} />
        {isToday(range) && <Refresh />}
        {activeTab === "fields" && selectedWorkTypeIds.length === 1
            && !disableCondition ? <Workbook filename={getExcelFileName()}
                element={
                    <button disabled={disableCondition}>
                        <i size="lg"
                            disabled={disableCondition}
                            title="Download"
                            className={"mt-2 fa fa-cloud-download fa-2x pt-1"}
                            style={{ cursor: `${disableCondition ? "not-allowed" : "pointer"}`, color: "#8d8d8d", opacity: `${disableCondition ? "0.3" : "1"}` }}>
                        </i></button>}
            >
            {getWorkSheets()}
        </Workbook> : <div style={{ marginLeft: '46px' }}></div>
        }
        <div className='d-flex'>
            {(selectedPlanType === 2 || isAllPlan) &&
                <img src={require("../../../../assets/images/seed_history.svg")}
                    className="load-history-icon"
                    alt="seed-history-icon"
                    onClick={() => handleSeedHistory()}
                />}

            {(selectedPlanType === 5 || isAllPlan) &&
                <img src={require("../../../../assets/images/logistics.svg")}
                    className="load-history-icon"
                    alt="logistic-history-icon"
                    onClick={() => handleLogisticHistory()}
                />}

            {(selectedPlanType !== 2 && selectedPlanType !== 5) &&
                <img src={require("../../../../assets/images/load_history.svg")}
                    className="load-history-icon"
                    alt="load-history-icon"
                    onClick={() => handleLoadHistory()}
                />}
        </div>
    </Row >;
};
export default Header;